import React, { useState, useEffect, useCallback, useRef, forwardRef, useImperativeHandle } from "react";
import GridLayout from "react-grid-layout";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";
import { Home, LampDesk, Toilet, Utensils } from "lucide-react";
// Assuming HActionButton is in the same folder or path is adjusted

const mobileStyles = `
.scrollbar-hide {
    -ms-overflow-style: none;
    scrollbar-width: none;
}
.scrollbar-hide::-webkit-scrollbar {
    display: none;
}
.floorplan-grid .react-grid-item {
    touch-action: manipulation;
    transition: all 0.2s ease;
}
.floorplan-grid .react-grid-item:active {
    transform: scale(1.02);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}
.floorplan-grid .react-grid-item.react-grid-placeholder {
    background: rgba(59, 130, 246, 0.2) !important;
    border: 2px dashed rgba(59, 130, 246, 0.5) !important;
    border-radius: 8px;
    animation: pulse 2s infinite;
}
@keyframes pulse {
    0%, 100% { opacity: 0.8; }
    50% { opacity: 0.4; }
}
@media (max-width: 767px) {
    .floorplan-grid .react-grid-item {
        border-radius: 8px !important;
        border-width: 2px !important;
        font-size: 10px !important;
    }
    .floorplan-grid .react-grid-item:hover {
        transform: none;
    }
}
`;

if (typeof document !== "undefined" && !document.getElementById("floorplan-mobile-styles")) {
  const styleElement = document.createElement("style");
  styleElement.id = "floorplan-mobile-styles";
  styleElement.textContent = mobileStyles;
  document.head.appendChild(styleElement);
}

// Simplified Floor plan element types
const ELEMENT_TYPES = {
  ROOM: { label: "Room", defaultW: 4, defaultH: 3 },
  DESK: { label: "Desk", defaultW: 2, defaultH: 1 },
  // WALL: { label: 'Wall', defaultW: 4, defaultH: 1 },
  BATHROOM: { label: "Bathroom", defaultW: 2, defaultH: 2 },
  KITCHEN: { label: "Kitchen", defaultW: 2, defaultH: 2 },
};

// Default icons if not provided by parent
const DEFAULT_ELEMENT_ICONS = {
  ROOM: <Home className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600" />,
  DESK: <LampDesk className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600" />,
  BATHROOM: <Toilet className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600" />,
  KITCHEN: <Utensils className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600" />,
};

const MAX_COLS = 20;
const ROW_HEIGHT = 30;
const GRID_CONTAINER_PADDING = 10; // px

const HSimpleFloorPlan = forwardRef(
  (
    {
      // onSave, // No longer needed as save is triggered externally
      initialLayout = [],
      className = "",
      onElementSelect,
      elementIcons,
      useIcons = true,
      compactType = null,
      preventCollision = true,
      getElementIcon,
      readOnly = false, // New prop for read-only mode
    },
    ref
  ) => {
    const [layout, setLayout] = useState([]);
    const [nextId, setNextId] = useState(0);
    const [isMounted, setIsMounted] = useState(false);
    const gridContainerRef = useRef(null);
    const [gridWidth, setGridWidth] = useState(0);
    const [isMobile, setIsMobile] = useState(false);

    // Check if device is mobile
    useEffect(() => {
      const checkMobile = () => {
        setIsMobile(window.innerWidth < 768);
      };
      checkMobile();
      window.addEventListener("resize", checkMobile);
      return () => window.removeEventListener("resize", checkMobile);
    }, []);

    // Expose functions to the parent component via ref
    useImperativeHandle(ref, () => ({
      getSaveData: () => {
        return {
          layout: layout,
          timestamp: new Date().toISOString(),
        };
      },
      getElementData: elementId => {
        return layout.find(item => item.i === elementId);
      },
      updateElementLabel: (elementId, newLabel) => {
        setLayout(prev => prev.map(item => (item.i === elementId ? { ...item, label: newLabel } : item)));
      },
    }));

    const generateDefaultRoom = useCallback(() => {
      const defaultRoom = [
        {
          i: "ROOM_0",
          x: 1,
          y: 1,
          w: ELEMENT_TYPES.ROOM.defaultW,
          h: ELEMENT_TYPES.ROOM.defaultH,
          elementType: "ROOM",
          label: "Main Room",
          static: false,
        },
      ];
      setNextId(1); // Start nextId after the default room
      return defaultRoom;
    }, []);

    useEffect(() => {
      if (initialLayout && initialLayout.length > 0) {
        setLayout(initialLayout);
        const maxId = initialLayout.reduce((max, item) => {
          const num = parseInt(item.i.split("_").pop(), 10);
          return isNaN(num) ? max : Math.max(max, num);
        }, -1);
        setNextId(maxId + 1);
      } else {
        setLayout(generateDefaultRoom());
      }
      setIsMounted(true);
    }, [initialLayout, generateDefaultRoom]);

    useEffect(() => {
      const calculateWidth = () => {
        if (gridContainerRef.current) {
          setGridWidth(gridContainerRef.current.offsetWidth - GRID_CONTAINER_PADDING * 2);
        }
      };
      calculateWidth();

      const resizeHandler = () => {
        calculateWidth();
        setIsMobile(window.innerWidth < 768);
      };

      window.addEventListener("resize", resizeHandler);
      return () => window.removeEventListener("resize", resizeHandler);
    }, [isMounted]);

    const addElement = (e, elementType) => {
      e.preventDefault();
      e.stopPropagation();

      const config = ELEMENT_TYPES[elementType];
      if (!config) return;

      const newElement = {
        i: `${elementType}_${nextId}`,
        x: (layout.length * 2) % MAX_COLS, // Basic stacking logic
        y: Math.floor((layout.length * 2) / MAX_COLS),
        w: config.defaultW,
        h: config.defaultH,
        elementType: elementType,
        label: config.label,
        static: false,
      };

      setLayout(prev => [...prev, newElement]);
      setNextId(prev => prev + 1);
    };

    const removeElement = (e, elementId) => {
      e.preventDefault();
      e.stopPropagation();
      setLayout(prev => prev.filter(item => item.i !== elementId));
    };

    const onLayoutChange = newLayout => {
      const updatedLayout = newLayout.map(item => {
        const existingItem = layout.find(l => l.i === item.i);
        return {
          ...item,
          elementType: existingItem?.elementType || "ROOM",
          label: existingItem?.label || "Room",
        };
      });
      setLayout(updatedLayout);
    };

    const handleDoubleClick = (e, elementId) => {
      e.preventDefault();
      e.stopPropagation();
      if (onElementSelect) {
        onElementSelect(elementId);
      }
    };

    const renderIcon = elementType => {
      if (getElementIcon) {
        return getElementIcon(elementType);
      }

      if (elementIcons && elementIcons[elementType]) {
        return elementIcons[elementType];
      }

      return DEFAULT_ELEMENT_ICONS[elementType] || <Home className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600" />;
    };

    const generateDOM = () => {
      return layout.map(item => {
        const elementTypeConfig = ELEMENT_TYPES[item.elementType] || ELEMENT_TYPES.ROOM;
        const icon = renderIcon(item.elementType);

        // Always use grey background regardless of element type
        const elementBgColor = "#f3f4f6"; // Light grey background for all elements

        return (
          <div
            key={item.i}
            onDoubleClick={e => handleDoubleClick(e, item.i)}
            style={{
              backgroundColor: elementBgColor,
              borderRadius: "4px",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              color: "#374151", // Text color
              fontSize: isMobile ? "8px" : "10px",
              fontWeight: "bold",
              cursor: "move",
              overflow: "hidden",
              border: "1px solid rgba(0,0,0,0.1)",
              position: "relative",
              minHeight: isMobile ? "40px" : "auto",
            }}
          >
            {/* Always show icon as the main indicator of room type */}
            <div className={isMobile ? "mb-0.5" : "mb-1"}>{icon}</div>
            <span
              style={{
                pointerEvents: "none",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                padding: isMobile ? "0 2px" : "0 4px",
                lineHeight: isMobile ? "1" : "normal",
              }}
            >
              {item.label || elementTypeConfig.label}
            </span>
            <button
              type="button"
              onClick={e => removeElement(e, item.i)}
              className="absolute top-0 right-0 p-0.5 bg-black bg-opacity-30 hover:bg-opacity-50 text-white rounded-bl-md rounded-tr-sm text-xs leading-none transition-all duration-200"
              style={{
                width: isMobile ? "24px" : "16px",
                height: isMobile ? "24px" : "16px",
                fontSize: isMobile ? "14px" : "10px",
                minTouchTarget: isMobile ? "44px" : "auto",
              }}
              title="Remove"
            >
              ×
            </button>
          </div>
        );
      });
    };

    if (!isMounted) {
      return <div className="p-4 text-center">Loading Floor Plan Editor...</div>;
    }

    return (
      <div className={`flex flex-col bg-white rounded-lg border shadow-sm ${className} h-full`}>
        {/* Mobile: Horizontal Toolbar at top, Desktop: Main content with sidebar */}
        {isMobile ? (
          <>
            {/* Mobile: Horizontal toolbar - only show if not read-only */}
            {!readOnly && (
              <div className="border-b bg-gray-50 p-2 overflow-x-auto scrollbar-hide">
                <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Elements</h4>
                <div className="flex space-x-2 min-w-max pb-1">
                  {Object.entries(ELEMENT_TYPES).map(([key, config]) => (
                    <button
                      key={key}
                      type="button"
                      onClick={e => addElement(e, key)}
                      title={`Add ${config.label}`}
                      className="flex-shrink-0 flex flex-col items-center justify-center space-y-1 p-3 rounded-lg border transition-colors bg-white border-gray-200 hover:bg-gray-100 active:bg-gray-200 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-16 min-h-16 shadow-sm"
                    >
                      <div className="flex-shrink-0">{renderIcon(key)}</div>
                      <span className="text-xs truncate leading-tight">{config.label}</span>
                    </button>
                  ))}
                </div>
                <p className="mt-2 text-xs text-gray-500">Tap to add • Double-tap to rename</p>
              </div>
            )}

            {/* Mobile: Main Grid Area */}
            <div
              ref={gridContainerRef}
              className="flex-1 bg-gray-100 overflow-auto"
              style={{
                padding: "8px",
                minHeight: "400px",
                WebkitOverflowScrolling: "touch", // Smooth scrolling on iOS
              }}
            >
              {gridWidth > 0 ? (
                <GridLayout
                  className="layout floorplan-grid bg-white shadow-inner rounded-md"
                  layout={layout}
                  onLayoutChange={onLayoutChange}
                  cols={10} // Slightly reduced for mobile
                  rowHeight={40} // Slightly larger for touch
                  width={gridWidth}
                  isDraggable={!readOnly}
                  isResizable={false} // Disable resizing on mobile for better touch experience
                  margin={[4, 4]}
                  containerPadding={[8, 8]}
                  compactType={compactType}
                  preventCollision={preventCollision}
                  style={{
                    border: "2px dashed #cbd5e1",
                    minHeight: "100%",
                    touchAction: "manipulation", // Improve touch handling on mobile
                    borderRadius: "6px",
                  }}
                >
                  {generateDOM()}
                </GridLayout>
              ) : (
                <div className="text-center p-4">Calculating editor size...</div>
              )}
            </div>
          </>
        ) : (
          // Desktop: Vertical sidebar with main content
          <div className="flex flex-1 overflow-hidden">
            {!readOnly && (
              <div className="w-48 border-r bg-gray-50 p-3 flex flex-col flex-shrink-0 overflow-y-auto">
                <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Elements</h4>
                <div className="space-y-1">
                  {Object.entries(ELEMENT_TYPES).map(([key, config]) => (
                    <button
                      key={key}
                      type="button"
                      onClick={e => addElement(e, key)}
                      title={`Add ${config.label}`}
                      className="w-full flex items-center space-x-2 p-2 rounded border transition-colors bg-white border-gray-200 hover:bg-gray-100 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                    >
                      <div className="flex-shrink-0">{renderIcon(key)}</div>
                      <span className="truncate">{config.label}</span>
                    </button>
                  ))}
                </div>
                <p className="mt-3 text-xs text-gray-500">Click an element above to add it to the floor plan.</p>
                <p className="mt-1 text-xs text-gray-500">Double-click an element to rename it.</p>
              </div>
            )}

            {/* Desktop: Main Grid Area */}
            <div
              ref={gridContainerRef}
              className="flex-1 p-2 bg-gray-100 overflow-auto"
              style={{ padding: `${GRID_CONTAINER_PADDING}px` }}
            >
              {gridWidth > 0 ? (
                <GridLayout
                  className="layout floorplan-grid bg-white shadow-inner"
                  layout={layout}
                  onLayoutChange={onLayoutChange}
                  cols={MAX_COLS}
                  rowHeight={ROW_HEIGHT}
                  width={gridWidth}
                  isDraggable={!readOnly}
                  isResizable={!readOnly}
                  margin={[5, 5]}
                  containerPadding={[0, 0]}
                  compactType={compactType}
                  preventCollision={preventCollision}
                  style={{ border: "1px dashed #cbd5e1", minHeight: "100%" }}
                >
                  {generateDOM()}
                </GridLayout>
              ) : (
                <div className="text-center p-4">Calculating editor size...</div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }
);

export default HSimpleFloorPlan;
